import { Controller } from '@hotwired/stimulus'
import JustValidate from 'just-validate'

export default class extends Controller {
    submitted = false;

    connect() {
        this.element.addEventListener("captcha:success", () => this.onCaptchaSuccess());
        this.quillMap = new Map();

        this.element.addEventListener('submit', () => {
            this.submitted = true;
            this.quillMap.forEach((quill, id) => {
                const isValid = quill.getText().trim().length > 0;
                const el = document.getElementById(id);
                if (isValid) {
                    el.classList.remove('just-validate-error-field');
                } else {
                    el.classList.add('just-validate-error-field');
                }
            });
        });

        this.validator = new JustValidate(this.element, {
            validateBeforeSubmitting: true,
        });

        // For choices
        this.validator.addField('#category', [
            {
                rule: 'required',
                errorMessage: 'Please select an option',
            },
            {
                validator: (value) => {
                    const v = document.getElementById('category')?.value || '';
                    return v.trim() !== '';
                },
                errorMessage: 'Please select an option',
            }
        ]);

        // Revalidate when user changes the Choices select
        const hearSelect = document.getElementById('category');
        if (hearSelect) {
            hearSelect.addEventListener('change', () => {
                this.validator.revalidateField('#category');
            });
        }

        // WEBSITE normalizer + validator
        const website = document.getElementById('organization_website')

        // normalize on blur (add https:// if missing)
        if (website) {
            // normalize scheme on blur
            website.addEventListener('blur', () => {
                const raw = website.value.trim();
                if (raw && !/^https?:\/\//i.test(raw)) website.value = `https://${raw}`;
                this.validator.revalidateField('#organization_website');
            });

            // live revalidate on input
            website.addEventListener('input', () => {
                this.validator.revalidateField('#organization_website');
            });

            // strict rule (requires scheme + dot + TLD)
            this.validator.addField('#organization_website', [
                { rule: 'required', errorMessage: 'Website is required' },
                {
                    validator: (value) => {
                        const v = value.trim();
                        const withScheme = /^https?:\/\//i.test(v) ? v : `https://${v}`;
                        return /^(https?:\/\/)([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(withScheme);
                    },
                    errorMessage: 'Please enter a valid website URL (e.g. https://example.org)'
                }
            ]);
        }

        this.element.querySelectorAll('input[required]').forEach(input => {
            const rules = [
                {
                    rule: 'required',
                    errorMessage: `${input.placeholder || 'Field'} is required`,
                }
            ];

            if (input.type === 'email') {
                rules.push({
                    rule: 'email',
                    errorMessage: 'Please enter a valid email address',
                });
            }

            if (input.type === 'url' && input.id !== 'organization_website') {
                rules.push({
                    rule: 'customRegexp',
                    value: /^(https?:\/\/)?([\w.-]+)+(:\d+)?(\/([\w/_-]*(\?\S+)?)?)?$/,
                    errorMessage: 'Please enter a valid URL',
                });
            }

            if (input.type === 'number') {
                rules.push({
                    rule: 'number',
                    errorMessage: 'Please enter a valid number',
                });
            }

            // You can add more type rules as needed...

            this.validator.addField(`#${input.id}`, rules);
        });

        // Listen for each quill instance as it initializes
        this.element.querySelectorAll('[data-controller="editor"]').forEach(editorEl => {
            const id = editorEl.id;
            editorEl.addEventListener('quill:ready', event => {
                this.quillMap.set(id, event.detail.quill);
                event.detail.quill.on('text-change', () => {
                    const quill = this.quillMap.get(id);
                    if (quill && quill.getText().trim().length > 0) {
                        document.getElementById(id).classList.remove('just-validate-error-field');
                    } else {
                        document.getElementById(id).classList.add('just-validate-error-field');
                    }
                });
            });
        });

        // Now safe to use map in onValidate
        this.validator.onValidate(() => {
            this.quillMap.forEach((quill, id) => {
                const field = Object.values(this.validator.fields).find(f => f.elem?.id === id);
                if (!field || (!field.touched && !this.submitted)) return;

                const isValid = quill.getText().trim().length > 0;
                const el = document.getElementById(id);

                if (isValid) {
                    el.classList.remove('just-validate-error-field');
                } else {
                    el.classList.add('just-validate-error-field');
                }
            });
        });



        // ----- reCAPTCHA explicit render -----
        this.widgetEl = document.getElementById('charity-partner-recaptcha')
        this.widgetId = null

        const tryRender = () => {
            if (window.grecaptcha?.render && this.widgetEl && this.widgetId == null) {
                this.widgetId = grecaptcha.render(this.widgetEl, {
                    sitekey: this.widgetEl.dataset.sitekey,
                    size: 'invisible',
                    badge: 'bottomright',            // or 'bottomleft' / 'inline'
                    callback: (token) => this.onCaptchaSuccess(token)
                })
            } else if (this.widgetId == null) {
                setTimeout(tryRender, 50)
            }
        }
        tryRender()

        // Turbo snapshot hygiene to avoid stale iframes
        this.beforeCache = () => {
            if (this.widgetId != null && window.grecaptcha?.reset) {
                try { grecaptcha.reset(this.widgetId) } catch (_) {}
            }
            // optional: clear container to force clean re-render on revisit
            // this.widgetEl && (this.widgetEl.innerHTML = "")
            // this.widgetId = null
        }
        document.addEventListener("turbo:before-cache", this.beforeCache)

    }

    submit(event) {
        event.preventDefault();
        this.submitted = true;

        // Normalize website field
        const website = document.getElementById('organization_website')
        if (website) {
            const raw = website.value.trim()
            if (raw && !/^https?:\/\//i.test(raw)) website.value = `https://${raw}`
        }

        if (!this.element.checkValidity()) {
            this.element.classList.add('was-validated');
            return;
        }

        // Trigger invisible reCAPTCHA; loader will show on "captcha:success"
        if (window.grecaptcha?.execute) {
            window.grecaptcha.execute();
        } else {
            // No captcha, just show loader + submit
            this.application.getControllerForElementAndIdentifier(
                this.element,
                "form-loader"
            )?.showLoader();
            this.element.submit();
        }
    }

    onCaptchaSuccess(token) {
        // Show loader
        this.application.getControllerForElementAndIdentifier(
            this.element,
            "form-loader"
        )?.showLoader();

        // Actually submit the form now
        this.element.submit();
    }
    disconnect() {
        this.element.removeEventListener("captcha:success", this.onCaptchaSuccess);
        this.validator.destroy();
        this.quillMap?.clear();
    }
}